import DesktopAwareNotification from "../core-components/DesktopAwareNotification";
import React, {useEffect, useRef, useState} from "react";
import {createPortal} from "react-dom";
import {BarChart3, Video, X} from "lucide-react";
import {useNavigate} from "react-router-dom";
import {collection, getDocs, limit, query, where} from "firebase/firestore";
import {db} from "../../Security/firebase-config";
import {eventSlots} from "../../utils/eventSetup";
import "./EventSetup.css";

export default function EventLinkedItems({event, teamId}) {
  const navigate = useNavigate();
  const [error, setError] = useState("");
  const [busy, setBusy] = useState(false);
  const [kind, setKind] = useState(null);
  const dialog = useRef(null);
  const desktop = !!window.desktopAPI;
  const slots = eventSlots(event).map((slot, index) => ({...slot, number: index + 1}));
  const recordings = slots.filter(slot => slot.matchId || (desktop && slot.localMatchId));
  const stats = slots.filter(slot => slot.statsId);
  const choices = kind === "recordings" ? recordings : stats;
  useEffect(() => {
    if (!kind) return undefined;
    const previous = document.activeElement;
    const overflow = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    dialog.current?.querySelector("button")?.focus();
    return () => {document.body.style.overflow = overflow; previous?.focus?.();};
  }, [kind]);

  async function openSlot(slot, resource) {
    setBusy(true); setError("");
    const enc = encodeURIComponent;
    try {
      if (resource === "stats") {
        navigate("/team-stats?teamId=" + enc(teamId) + "&match=" + enc(slot.statsId));
      } else if (slot.localMatchId && event.calendar === "personal") {
        navigate("/review?map=" + enc(slot.mapId || "") + "&localMatch=" + enc(slot.localMatchId));
      } else {
        const videos = await getDocs(query(collection(db, "teams", teamId, "maps", slot.mapId, "videos"), where("matchId", "==", slot.matchId), limit(1)));
        if (videos.empty && desktop) {
          navigate("/review?teamId=" + enc(teamId) + "&map=" + enc(slot.mapId) + "&localMatch=" + enc(slot.matchId));
        } else {
          if (videos.empty) throw new Error("No recording is available for this map yet.");
          const videoId = videos.docs[0].id;
          navigate(desktop ? "/review?teamId=" + enc(teamId) + "&map=" + enc(slot.mapId) + "&video=" + enc(videoId)
            : "/review/" + enc(slot.mapId) + "/" + enc(videoId) + "?teamId=" + enc(teamId));
        }
      }
      setKind(null);
    } catch (failure) {setError(failure.message || "Could not open linked content.");}
    finally {setBusy(false);}
  }
  function choose(resource, linked) {
    setError("");
    if (slots.length > 1) setKind(resource);
    else void openSlot(linked[0], resource);
  }
  return <span className="EventLinkedItems" onClick={e => e.stopPropagation()}>
    {recordings.length > 0 && <button type="button" disabled={busy}
      aria-label={"Open recordings (" + recordings.length + (recordings.length === 1 ? " map)" : " maps)")}
      title={"Recordings linked on " + recordings.length + (recordings.length === 1 ? " map" : " maps")}
      aria-haspopup={slots.length > 1 ? "dialog" : undefined} onClick={() => choose("recordings", recordings)}>
      <Video size={15} aria-hidden="true"/><span>{recordings.length}</span>
    </button>}
    {stats.length > 0 && <button type="button" disabled={busy}
      aria-label={"Open Team Stats (" + stats.length + (stats.length === 1 ? " entry)" : " entries)")}
      title={stats.length + " linked Team Stats " + (stats.length === 1 ? "entry" : "entries")}
      aria-haspopup={slots.length > 1 ? "dialog" : undefined} onClick={() => choose("stats", stats)}>
      <BarChart3 size={15} aria-hidden="true"/><span>{stats.length}</span>
    </button>}
    {error && !kind && <DesktopAwareNotification as="span" role="alert">{error}</DesktopAwareNotification>}
    {kind && createPortal(<div className="EventLinkedItems__backdrop" onClick={e => {e.stopPropagation(); if (!busy) setKind(null);}}>
      <section className="EventLinkedItems__dialog" ref={dialog} role="dialog" aria-modal="true" aria-label="Choose a linked map"
        onClick={e => e.stopPropagation()} onKeyDown={e => {
          if (e.key === "Escape") {e.stopPropagation(); if (!busy) setKind(null);}
          if (e.key === "Tab") {
            const buttons = Array.from(dialog.current.querySelectorAll("button:not(:disabled)"));
            const first = buttons[0], last = buttons[buttons.length - 1];
            if (!buttons.length) {e.preventDefault(); return;}
            if (e.shiftKey && document.activeElement === first) {e.preventDefault(); last.focus();}
            else if (!e.shiftKey && document.activeElement === last) {e.preventDefault(); first.focus();}
          }
        }}>
        <header><h3>{kind === "recordings" ? "Open recordings" : "Open Team Stats"}</h3>
          <button className={'app-cancel-control'} type="button" aria-label="Close map selector" disabled={busy} onClick={() => setKind(null)}><X size={18}/></button>
        </header>
        <p>Select the map you want to open.</p>
        <div className="EventLinkedItems__choices">{choices.map(slot => <button type="button" key={slot.id} disabled={busy} onClick={() => openSlot(slot, kind)}>
          {kind === "recordings" ? <Video size={18} aria-hidden="true"/> : <BarChart3 size={18} aria-hidden="true"/>}
          <span>Map {slot.number} · {slot.mapName || "unknown"}</span>
        </button>)}</div>
        {!choices.length && <p>No linked {kind === "recordings" ? "recordings" : "Team Stats"} remain for this event.</p>}
        {busy && <p role="status">Opening…</p>}
        {error && <DesktopAwareNotification as="p" role="alert">{error}</DesktopAwareNotification>}
      </section>
    </div>, document.body)}
  </span>;
}
